import {
	TAG_EDIT_SET_TAGS,
	TAG_EDIT_SET_TAG,
	TAG_EDIT_SET_IS_SAVING,
	TAG_EDIT_UPDATE_PROPS,
	TAG_EDIT_EDIT_TIMESHIFT,
} from '../../constants/mutations';
import { bus, EVENT } from '@videotools/js/events';
import I18n from '@legacy/js/vendor/I18n';
import Helpers from '@legacy/js/helpers';
import EventTag from '@videotools/js/api/entities/EventTag';

const i18n = new I18n();
const helpers = new Helpers();

export default {
	namespaced: true,
	state:      () => ({
		selectedTags:       [],
		isEditingTimeshift: false,
		isSaving:           false,
	}),
	mutations: {
		[TAG_EDIT_SET_TAG]: (state, tag) => {
			state.selectedTags = [new EventTag(tag)];
		},
		[TAG_EDIT_SET_TAGS]: (state, tags) => {
			state.selectedTags = tags;
		},
		[TAG_EDIT_EDIT_TIMESHIFT]: (state, active) => {
			state.isEditingTimeshift = active;
		},
		[TAG_EDIT_SET_IS_SAVING]: (state, flag) => {
			state.isSaving = flag;
		},
		[TAG_EDIT_UPDATE_PROPS]: (state, props) => {
			const [currentTag, ...rest] = state.selectedTags;
			const tag = new EventTag(currentTag);
			Object.assign(tag, props);
			state.selectedTags = [tag, ...rest];
		},
	},
	actions: {
		editEventTag: ({ commit, rootGetters }, tag) => {
			const eventTag = rootGetters['event/tagById'](tag?.id);
			commit(TAG_EDIT_SET_TAG, eventTag);
		},
		bulkEditEventTags: ({ commit, rootGetters }, tags = []) => {
			const tagIds = tags?.map(tag => tag.id);
			const eventTags = rootGetters['event/tagsByIds'](tagIds);
			commit(TAG_EDIT_SET_TAGS, eventTags || []);
		},
		editTimeshift: ({ commit, rootGetters }, tags = []) => {
			const tagIds = tags?.map(tag => tag.id);
			const eventTags = rootGetters['event/tagsByIds'](tagIds);
			commit(TAG_EDIT_SET_TAGS, eventTags || []);
			commit(TAG_EDIT_EDIT_TIMESHIFT, true, eventTags || []);
		},
		async timeshift({ commit, dispatch }, { selectedTags, timeShift }) {
			try {
				commit(TAG_EDIT_SET_IS_SAVING, true);
				// has time shifting of tags
				if (timeShift) {
					await dispatch('doTimeShift', { selectedTags, timeShift });
					bus.publish(EVENT.SUCCESS, helpers.ucfirst(i18n.trans('notifications.tag_timeshift')));
				}
			} finally {
				dispatch('clearSelection');
				commit(TAG_EDIT_SET_IS_SAVING, false);
			}
		},
		async bulkSave({ commit, dispatch }, { tagUpdates }) {
			try {
				commit(TAG_EDIT_SET_IS_SAVING, true);
				// has tag updates
				if (tagUpdates) {
					await dispatch('doBulkUpdate', tagUpdates);
					bus.publish(EVENT.SUCCESS, helpers.ucfirst(i18n.trans('notifications.tag_bulk_updated')));
				}
			} finally {
				dispatch('clearSelection');
				commit(TAG_EDIT_SET_IS_SAVING, false);
			}
		},
		async doBulkUpdate({ dispatch, rootGetters }, { tags, tagType, team, player, isIos }) {
			const body = {};
			if (tagType) {
				body.name = tagType.name;
				body.tagId = tagType.id;
			}
			if (team) {
				body.teamId = team.id;
			}
			if (player) {
				body.playerId = player.id;
			}

			const event = rootGetters['event/event'];
			await dispatch('event/updateEventTags', {
				eventId:     event.id,
				tags,
				body,
				backendSnap: !!isIos,
			}, { root: true });
		},
		async doTimeShift({ dispatch, rootGetters }, { selectedTags, timeShift }) {
			const event = rootGetters['event/event'];
			await dispatch(
				'event/timeShiftEventTags',
				{ eventId: event.id, offset: timeShift.offset, tagIds: Object.values(selectedTags.map(tag => tag.id)) },
				{ root: true }
			);

			dispatch('event/loadEventTags', event.id, { root: true });
		},
		clearSelection({ commit }) {
			commit(TAG_EDIT_SET_TAGS, []);
			commit(TAG_EDIT_EDIT_TIMESHIFT, false);
		},
		updateSelectedTagProps({ commit, getters }, {
			name,
			tag,
			team,
			player,
			timestamp,
			secondsBefore,
			secondsAfter,
			rating,
		}) {
			const eventTag = getters.selectedTag;
			let props = {};

			if (Number.isFinite(secondsBefore) && secondsBefore !== eventTag.secondsBefore) {
				props = { ...props, secondsBefore: Math.round(secondsBefore) };
			}
			if (Number.isFinite(secondsAfter) && secondsAfter !== eventTag.secondsAfter) {
				props = { ...props, secondsAfter: Math.round(secondsAfter) };
			}
			if (Number.isFinite(timestamp) && timestamp !== eventTag.timestamp) {
				props = { ...props, timestamp: Math.round(timestamp) };
			}
			if (name !== undefined && name !== eventTag.name) {
				props = { ...props, name };
			}
			if (tag !== undefined) {
				props = { ...props, tag };
			}
			if (team !== undefined) {
				props = { ...props, team };
			}
			if (player !== undefined) {
				props = { ...props, player };
			}
			if (rating !== undefined) {
				props = { ...props, rating };
			}

			if (Object.keys(props).length > 0) {
				commit(TAG_EDIT_UPDATE_PROPS, props);
			}
		},
		discardSingleEdit({ getters, dispatch, rootGetters }) {
			const eventTag = rootGetters['event/tagById'](getters.selectedTag.id);
			if (eventTag) {
				dispatch('editEventTag', eventTag);
			}
		},
	},
	getters: {
		selectedTag:         (state, getters) => getters.isSingleEdit ? state.selectedTags[0] : null,
		timelineSelectedTag: (state, getters, rootState, rootGetters) => {
			const selectedEventTag = getters.selectedTag;
			if (!selectedEventTag) {
				return null;
			}
			if (rootGetters['tags/isForTiming'](selectedEventTag.tag)) {
				return null;
			}

			return selectedEventTag;
		},
		selectedTags:       (state) => state.selectedTags,
		editableTags:       (state, getters, rootState, rootGetters) => [...rootGetters['event/eventTagsForEvent'], ...rootGetters['event/eventTagsForParticipant']],
		isSingleEdit:       (state) => state.selectedTags.length === 1 && !state.isEditingTimeshift,
		isTimeshiftEdit:    (state) => state.isEditingTimeshift,
		isBulkEdit:         (state) => state.selectedTags.length > 1 && !state.isEditingTimeshift,
		areAllTagsSelected: (state, getters) => {
			return getters.isBulkEdit && getters.editableTags.length === getters.selectedTags.length;
		},
		isSaving: (state) => state.isSaving,
	},
};
